import './form-date-range-field.scss';

import { Controller, useFormContext } from 'react-hook-form';
import { Dispatch, FC, SetStateAction, useMemo, useState } from 'react';
import { TextField, TextFieldProps, styled } from '@mui/material';

import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { FormFieldContainer } from './form-field-container';
import { NewConvergeInputLabel } from './new-converge-input-label';
import moment from 'moment';

type FormDateRangeFieldProps = {
  name: string;
  containerWidth?: number;
  views?: Array<'day' | 'month' | 'year'>;
  minDate?: Date;
  maxDate?: Date;
  isReadonly?: boolean;
  setError: Dispatch<SetStateAction<boolean>>;
  onDateChange?: (
    value: Date | null,
    keyboardInputValue?: string | undefined
  ) => void;
} & TextFieldProps;

export const FormDateRangeField: FC<FormDateRangeFieldProps> = ({
  containerWidth = 6,
  setError,
  required,
  isReadonly,
  name,
  label,
  views = ['year', 'day'],
  minDate,
  maxDate,
  onDateChange,
  ...otherProps
}) => {
  const { control } = useFormContext();

  const formDateValue = control._formValues[name];

  const [endDateRangeError, setEndDateRangeError] = useState<string>('');
  const [startDateRangeError, setStartDateRangeError] = useState<string>('');

  const StyledTextField = useMemo(
    () => styled(TextField)`
      .MuiOutlinedInput-root {
        &.Mui-focused .MuiOutlinedInput-notchedOutline {
          border: 1px solid #23a892 !important;
        }
        &:hover .MuiOutlinedInput-notchedOutline {
          border: 1px solid #23a892 !important;
        }
        .MuiOutlinedInput-notchedOutline {
          border: 1px solid #4d4d4d !important;
        }
      }

      // Style for the dropdown options
      .MuiAutocomplete-popper {
        .MuiAutocomplete-option {
          &:hover {
            background-color: rgba(35, 168, 146, 0.1) !important;
          }
          &[aria-selected='true'] {
            background-color: rgba(35, 168, 146, 0.2) !important;
          }
          &.Mui-focused {
            background-color: rgba(35, 168, 146, 0.1) !important;
          }
        }
      }
    `,
    []
  );
  return (
    <Controller
      control={control}
      name={name}
      rules={{
        required,
      }}
      render={({
        field: { value, onChange, onBlur, ...otherFields },
        formState: { errors },
      }) => (
        <FormFieldContainer md={1}>
          <NewConvergeInputLabel
            label={label}
            required={required}
            className={errors?.root?.message ? 'error-message' : ''}
          />

          <DateRangePicker
            value={[
              formDateValue && formDateValue[0],
              formDateValue && formDateValue[1],
            ]}
            onChange={(newValue) => {
              onChange(newValue);
              setError(false);

              const pattern = /^\d{4}-\d{2}-\d{2}$/;
              if (newValue[0] && newValue[1] && newValue[1] < newValue[0]) {
                setEndDateRangeError('End date value is before start date');
                setError(true);
              } else if (newValue[0] === null) {
                setStartDateRangeError('Enter a valid date MM/DD/YYYY');
                setError(true);
              } else if (newValue[1] === null) {
                setEndDateRangeError('Enter a valid date MM/DD/YYYY');
                setError(true);
              } else if (
                newValue[0] &&
                !pattern.test(moment(newValue[0]).utc().format('YYYY-MM-DD'))
              ) {
                setStartDateRangeError('Enter a valid date MM/DD/YYYY');
                setError(true);
              } else if (
                newValue[1] &&
                !pattern.test(moment(newValue[1]).utc().format('YYYY-MM-DD'))
              ) {
                setEndDateRangeError('Enter a valid date MM/DD/YYYY');
                setError(true);
              } else if (
                newValue[1] !== null &&
                pattern.test(moment(newValue[1]).utc().format('YYYY-MM-DD'))
              ) {
                setEndDateRangeError('');
              } else if (
                newValue[0] !== null &&
                pattern.test(moment(newValue[0]).utc().format('YYYY-MM-DD'))
              ) {
                setStartDateRangeError('');
              }
            }}
            renderInput={(startProps, endProps) => (
              <>
                <StyledTextField
                  fullWidth
                  size="small"
                  name={`${name}_start`}
                  data-testid="time_range_start"
                  className="date-range-picker-text-field-container"
                  {...startProps}
                  {...otherProps}
                  error={!!startDateRangeError}
                  defaultValue={new Date()}
                  helperText={
                    errors[name]
                      ? (errors[name]?.message as unknown as string)
                      : startDateRangeError ?? ''
                  }
                />
                <StyledTextField
                  fullWidth
                  size="small"
                  name={`${name}_end`}
                  data-testid="time_range_end"
                  className="date-range-picker-text-field-second-container"
                  {...endProps}
                  {...otherProps}
                  error={!!endDateRangeError}
                  helperText={
                    errors[name]
                      ? (errors[name]?.message as unknown as string)
                      : endDateRangeError ?? ''
                  }
                />
              </>
            )}
          />
        </FormFieldContainer>
      )}
    />
  );
};
